import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { DataTable, FilterBar, StatsCard, PaginationControls, SearchInput, LoadingState, ConfirmModal } from '../../components/admin/shared';
import { FileText, Clock, CheckCircle, XCircle, Eye, Download } from 'lucide-react';
import educentialApi from '../../api/educentialApi';

const Documents = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20 });
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(null);
  const [rejectionNote, setRejectionNote] = useState('');

  const [filters, setFilters] = useState({
    feature: searchParams.get('feature') || '',
    status: searchParams.get('status') || '',
    documentType: '',
    search: ''
  });

  const features = [
    { value: 'overseas', label: 'Overseas Education' },
    { value: 'career', label: 'Career Counseling' },
    { value: 'skills', label: 'Skill Development' },
    { value: 'test-prep', label: 'Test Preparation' },
    { value: 'scholarships', label: 'Scholarship Finder' },
    { value: 'visa', label: 'Visa Assistance' },
    { value: 'accommodation', label: 'Accommodation' },
    { value: 'pre-departure', label: 'Pre-Departure' },
    { value: 'post-arrival', label: 'Post-Arrival' },
    { value: 'alumni', label: 'Alumni Network' }
  ];

  useEffect(() => {
    loadDocuments();
  }, [filters, pagination.page, pagination.limit]);

  const loadDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.feature) params.append('feature', filters.feature);
      if (filters.status) params.append('status', filters.status);
      if (filters.documentType) params.append('documentType', filters.documentType);
      if (filters.search) params.append('search', filters.search);
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);

      const response = await educentialApi.get(`/api/admin/documents?${params.toString()}`);
      setDocuments(response.data.data || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0
      }));

      // Load stats
      const statsResponse = await educentialApi.get('/api/admin/stats');
      setStats(statsResponse.data.documents);
    } catch (err) {
      console.error('Error loading documents:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load documents';
      setError(errorMessage);

      // If unauthorized, redirect to login
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentAction = async (documentId, action, note = '') => {
    try {
      await educentialApi.patch(`/api/admin/documents/${documentId}`, {
        status: action,
        reviewNote: note
      });
      await loadDocuments();
      setSelectedDocuments([]);
    } catch (error) {
      console.error('Error updating document:', error);
      alert('Failed to update document. Please try again.');
    }
  };

  const handleBulkAction = async (action) => {
    setShowBulkModal(null);
    try {
      await educentialApi.post('/api/admin/documents/bulk-review', {
        documentIds: selectedDocuments,
        status: action,
        reviewNote: action === 'rejected' ? rejectionNote : ''
      });
      await loadDocuments();
      setSelectedDocuments([]);
      setRejectionNote('');
      alert(`${selectedDocuments.length} documents ${action} successfully!`);
    } catch (error) {
      console.error('Error performing bulk action:', error);
      alert('Failed to perform bulk action. Please try again.');
    }
  };

  const handleSelectAll = () => {
    if (selectedDocuments.length === documents.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(documents.map(doc => doc.id));
    }
  };

  const handleSelectDocument = (docId) => {
    setSelectedDocuments(prev =>
      prev.includes(docId)
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock },
      approved: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle }
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 text-xs font-medium rounded-full ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        <span className="capitalize">{status}</span>
      </span>
    );
  };

  const columns = [
    {
      key: 'select',
      label: (
        <input
          type="checkbox"
          checked={selectedDocuments.length === documents.length && documents.length > 0}
          onChange={handleSelectAll}
          className="rounded border-gray-300"
        />
      ),
      render: (doc) => (
        <input
          type="checkbox"
          checked={selectedDocuments.includes(doc.id)}
          onChange={() => handleSelectDocument(doc.id)}
          onClick={(e) => e.stopPropagation()}
          className="rounded border-gray-300"
        />
      )
    },
    {
      key: 'id',
      label: 'ID',
      render: (doc) => <span className="font-mono text-xs">#{doc.id}</span>
    },
    {
      key: 'documentType',
      label: 'Type',
      render: (doc) => <span className="text-sm font-medium">{doc.documentType}</span>
    },
    {
      key: 'student',
      label: 'Student',
      render: (doc) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{doc.studentName || 'N/A'}</div>
          <div className="text-xs text-gray-500">{doc.userEmail}</div>
        </div>
      )
    },
    {
      key: 'feature',
      label: 'Feature',
      render: (doc) => (
        <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded">
          {features.find(f => f.value === doc.service)?.label || doc.service}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (doc) => getStatusBadge(doc.status)
    },
    {
      key: 'uploadedAt',
      label: 'Uploaded',
      render: (doc) => (
        <span className="text-sm text-gray-600">
          {new Date(doc.uploadedAt || doc.createdAt).toLocaleDateString()}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (doc) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              window.open(doc.fileUrl, '_blank');
            }}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
            title="View"
          >
            <Eye className="w-4 h-4" />
          </button>
          {doc.status === 'pending' && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDocumentAction(doc.id, 'approved');
                }}
                className="p-1 text-green-600 hover:bg-green-50 rounded"
                title="Approve"
              >
                <CheckCircle className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const note = window.prompt('Rejection reason:');
                  if (note) handleDocumentAction(doc.id, 'rejected', note);
                }}
                className="p-1 text-red-600 hover:bg-red-50 rounded"
                title="Reject"
              >
                <XCircle className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      )
    }
  ];

  const filterConfig = [
    {
      name: 'feature',
      label: 'Feature',
      type: 'select',
      value: filters.feature,
      options: features
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      value: filters.status,
      options: [
        { value: 'pending', label: 'Pending Review' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' }
      ]
    },
    {
      name: 'documentType',
      label: 'Document Type',
      type: 'select',
      value: filters.documentType,
      options: [
        { value: 'passport', label: 'Passport' },
        { value: 'transcript', label: 'Academic Transcript' },
        { value: 'certificate', label: 'Certificate' },
        { value: 'cv', label: 'CV/Resume' },
        { value: 'sop', label: 'Statement of Purpose' }
      ]
    }
  ];

  if (loading && documents.length === 0) {
    return (
      <AdminLayout title="Documents">
        <LoadingState message="Loading documents..." />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Documents">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Document Management</h2>
          <p className="text-gray-600 mt-1">Review and manage all documents across all features</p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">Error Loading Documents</h3>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
              <button
                onClick={() => loadDocuments()}
                className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatsCard
            title="Total Documents"
            value={stats?.total || 0}
            icon={FileText}
          />
          <StatsCard
            title="Pending Review"
            value={stats?.pending || 0}
            icon={Clock}
          />
          <StatsCard
            title="Approved"
            value={stats?.approved || 0}
            icon={CheckCircle}
          />
          <StatsCard
            title="Rejected"
            value={stats?.rejected || 0}
            icon={XCircle}
          />
        </div>

        {/* Bulk Actions */}
        {selectedDocuments.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-900">
                {selectedDocuments.length} document{selectedDocuments.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowBulkModal('approved')}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                >
                  Approve Selected
                </button>
                <button
                  onClick={() => setShowBulkModal('rejected')}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                >
                  Reject Selected
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <SearchInput
          value={filters.search}
          onChange={(value) => setFilters(prev => ({ ...prev, search: value }))}
          placeholder="Search by student name or document type..."
        />

        {/* Filters */}
        <FilterBar
          filters={filterConfig}
          onFilterChange={(name, value) => setFilters(prev => ({ ...prev, [name]: value }))}
          onClear={() => setFilters({ feature: '', status: '', documentType: '', search: '' })}
        />

        {/* Documents Table */}
        <DataTable
          columns={columns}
          data={documents}
          loading={loading}
          emptyMessage="No documents found. Try adjusting your filters."
          onRowClick={(doc) => navigate(`/admin/applications/${doc.applicationId}`)}
        />

        {/* Pagination */}
        <PaginationControls
          currentPage={pagination.page}
          totalPages={Math.ceil(pagination.total / pagination.limit)}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          onItemsPerPageChange={(limit) => setPagination(prev => ({ ...prev, limit, page: 1 }))}
        />

        {/* Bulk Action Modals */}
        {showBulkModal === 'approved' && (
          <ConfirmModal
            isOpen={true}
            title="Approve Documents?"
            message={`Are you sure you want to approve ${selectedDocuments.length} document(s)?`}
            confirmText="Approve"
            cancelText="Cancel"
            onConfirm={() => handleBulkAction('approved')}
            onCancel={() => setShowBulkModal(null)}
          />
        )}

        {showBulkModal === 'rejected' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Reject {selectedDocuments.length} Document(s)?
              </h3>
              <textarea
                value={rejectionNote}
                onChange={(e) => setRejectionNote(e.target.value)}
                placeholder="Reason for rejection (optional)"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 mb-4"
                rows={4}
              />
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowBulkModal(null);
                    setRejectionNote('');
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleBulkAction('rejected')}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Reject Documents
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Documents;
